/**
 * Marker + copy helpers for `deck.details.synergy.profile`. Mirrors
 * `deckProfileMarkers.js` but targets the synergy profile shape described
 * in api-changes-synergy.md. Kept data-only so UI modules stay thin.
 */

const UNKNOWN_PATTERN_TONE = 'cool';

const PATTERN_META = {
    commanderCentric: { label: 'Commander anchors the web', tone: 'alert' },
    commanderDetached: { label: 'Commander sits apart', tone: 'warn' },
    commanderBonusHeavy: { label: 'Commander bonus carries weight', tone: 'warn' },
    hubDriven: { label: 'One card is the hub', tone: 'alert' },
    distributedWeb: { label: 'Spread evenly across many cards', tone: 'ok' },
    enablerDense: { label: 'Enabler-dense', tone: 'warn' },
    enablerThin: { label: 'Synergies stand on their own', tone: 'ok' },
    staticHeavy: { label: 'Static-heavy', tone: 'cool' },
    triggerHeavy: { label: 'Trigger-heavy', tone: 'alert' },
    activeHeavy: { label: 'Activated-ability heavy', tone: 'warn' },
    replacementDriven: { label: 'Replacement effects amplify', tone: 'warn' },
    shallowReach: { label: 'Few cards touch the web', tone: 'warn' },
    broadReach: { label: 'Most of the deck participates', tone: 'ok' },
    symbioticPair: { label: 'Tight two-card pairing', tone: 'alert' },
    chainedDependency: { label: 'Long enabler chains', tone: 'alert' },
    peakedDistribution: { label: 'One line carries weight', tone: 'alert' },
    diffuseDistribution: { label: 'Many small pieces stack up', tone: 'ok' },
    tightlyConcentrated: { label: 'Hinges on a few conditions', tone: 'alert' },
    genericTypeHeavy: { label: 'Broad "any spell" effects', tone: 'cool' },
    orphanHeavy: { label: 'Many cards sit outside the web', tone: 'warn' },
    supportRich: { label: 'Enablers have backup', tone: 'ok' },
    supportThin: { label: 'Enablers lack backup', tone: 'alert' },
};

function humanizeId(id) {
    if (!id) return '';
    return id
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (c) => c.toUpperCase())
        .trim();
}

export function deriveSynergyPatternChips(profile) {
    const patterns = Array.isArray(profile?.patterns) ? profile.patterns : [];
    return patterns.map((id) => {
        const meta = PATTERN_META[id];
        if (meta) return { key: id, label: meta.label, tone: meta.tone };
        return { key: id, label: humanizeId(id), tone: UNKNOWN_PATTERN_TONE };
    });
}

const TEMPO_COPY = {
    triggerHeavy: 'trigger-heavy',
    staticHeavy: 'static-heavy',
    activeHeavy: 'activation-heavy',
    reactiveHeavy: 'replacement-heavy',
    balanced: 'balanced',
};

const REACH_COPY = {
    none: 'sparse-reach',
    light: 'narrow-reach',
    moderate: 'moderate-reach',
    dense: 'broad-reach',
};

const COMMANDER_COPY = {
    detached: 'a detached commander',
    involved: 'an involved commander',
    central: 'a central commander',
    dominant: 'a dominant commander',
};

const REDUNDANCY_COPY = {
    fragile: 'fragile redundancy',
    conditional: 'conditional redundancy',
    stable: 'stable redundancy',
    redundant: 'redundant backups',
};

/**
 * Return a single-sentence description of the synergy web — the "one-line
 * elevator pitch" the API guide recommends:
 *   reach + tempo + commanderCentricity + redundancy
 */
export function deriveSynergyHeadline(profile) {
    if (!profile) return null;
    const reach = REACH_COPY[profile.reach];
    const tempo = TEMPO_COPY[profile.tempo];
    const commander = COMMANDER_COPY[profile.commanderCentricity];
    const redundancy = REDUNDANCY_COPY[profile.redundancy];

    if (!reach && !tempo && !commander && !redundancy) return null;

    const webDescriptor =
        reach && tempo ? `${reach}, ${tempo}` : reach || tempo || 'synergy';
    const pieces = [`Runs a ${webDescriptor} web`];
    if (commander) pieces.push(`with ${commander}`);
    if (redundancy) pieces.push(`and ${redundancy}`);
    return `${pieces.join(' ')}.`;
}

const DENSITY_LABELS = {
    none: 'None',
    light: 'Light',
    moderate: 'Moderate',
    dense: 'Dense',
};

const CONCENTRATION_LABELS = {
    diffuse: 'Diffuse',
    distributed: 'Distributed',
    concentrated: 'Concentrated',
    hyperConcentrated: 'Hyper-concentrated',
};

const WEB_TOPOLOGY_LABELS = {
    sparse: 'Sparse',
    chain: 'Chain',
    star: 'Star',
    mesh: 'Mesh',
};

const COMMANDER_CENTRICITY_LABELS = {
    detached: 'Detached',
    involved: 'Involved',
    central: 'Central',
    dominant: 'Dominant',
};

const RELIABILITY_LABELS = {
    fragile: 'Fragile',
    conditional: 'Conditional',
    stable: 'Stable',
    redundant: 'Redundant',
};

const TEMPO_LABELS = {
    balanced: 'Balanced',
    triggerHeavy: 'Trigger-heavy',
    staticHeavy: 'Static-heavy',
    activeHeavy: 'Active-heavy',
    reactiveHeavy: 'Reactive-heavy',
};

export const SYNERGY_ENUM_LABELS = {
    density: DENSITY_LABELS,
    reach: DENSITY_LABELS,
    enablerDependence: DENSITY_LABELS,
    concentration: CONCENTRATION_LABELS,
    webTopology: WEB_TOPOLOGY_LABELS,
    commanderCentricity: COMMANDER_CENTRICITY_LABELS,
    redundancy: RELIABILITY_LABELS,
    tempo: TEMPO_LABELS,
};

export function synergyEnumLabel(field, value) {
    if (!value) return null;
    const map = SYNERGY_ENUM_LABELS[field];
    return map?.[value] || humanizeId(value);
}
